'use client'

// Contact — v3 · "The Horizon"
// One animated line across the viewport at 50% vertical height.
// Breathes, follows cursor, pulses on typing, marks focused fields,
// blooms on submit. Everything else is minimal content-first scaffolding.

import React, {
  useRef, useState, useCallback, useEffect,
} from 'react'
import { useGSAP } from '@gsap/react'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

// ─── Tokens ──────────────────────────────────────────────────────────────────
const CREAM       = '#F5ECD8'
const CREAM_75    = 'rgba(245,236,216,0.75)'
const CREAM_50    = 'rgba(245,236,216,0.50)'
const CREAM_40    = 'rgba(245,236,216,0.40)'
const CREAM_30    = 'rgba(245,236,216,0.30)'
const CREAM_25    = 'rgba(245,236,216,0.25)'
const OLIVE       = '#5B7005'
const LIME        = '#9FAF08'
const ERROR_C     = 'rgba(178,68,40,0.85)'
const EARTH_TOP   = '#2A1F0D'
const EARTH_BOT   = '#1F1608'
const FONT_BODY   = 'var(--font-body)'
const FONT_DISP   = 'var(--font-display)'
const FONT_SERIF  = 'var(--font-serif)'

// ─── Crop brackets ───────────────────────────────────────────────────────────
function CropBracket({ corner }: { corner: 'tl' | 'tr' | 'bl' | 'br' }) {
  const d = {
    tl: 'M0 10 L0 0 L10 0',
    tr: 'M8 0 L18 0 L18 10',
    bl: 'M0 8 L0 18 L10 18',
    br: 'M8 18 L18 18 L18 8',
  }[corner]
  const pos = {
    tl: { top: 0,  left: 0  },
    tr: { top: 0,  right: 0 },
    bl: { bottom: 0, left: 0 },
    br: { bottom: 0, right: 0 },
  }[corner]
  return (
    <svg
      data-ct-crop
      aria-hidden="true"
      viewBox="0 0 18 18"
      style={{
        position: 'absolute', width: 18, height: 18, ...pos,
        pointerEvents: 'none', zIndex: 20,
      }}
    >
      <path d={d} fill="none" stroke={CREAM_25} strokeWidth={1} />
    </svg>
  )
}

// ─── Contact ─────────────────────────────────────────────────────────────────
const HORIZON_H = 60           // SVG wrapper height
const CENTER_Y  = HORIZON_H / 2 // path baseline y

type FieldName = 'name' | 'email' | 'message'

export default function Contact() {
  const t = useTranslations('contact')
  // ── Refs ────────────────────────────────────────────────────────────────
  const sectionRef   = useRef<HTMLElement>(null)
  const contentRef   = useRef<HTMLFormElement>(null)
  const horizonBoxRef = useRef<HTMLDivElement>(null)   // clip-path container
  const horizonSvgRef = useRef<SVGSVGElement>(null)
  const horizonPathRef = useRef<SVGPathElement>(null)
  const nameWrapRef    = useRef<HTMLDivElement>(null)
  const emailWrapRef   = useRef<HTMLDivElement>(null)
  const msgWrapRef     = useRef<HTMLDivElement>(null)
  const nameInputRef   = useRef<HTMLInputElement>(null)
  const emailInputRef  = useRef<HTMLInputElement>(null)
  const msgInputRef    = useRef<HTMLTextAreaElement>(null)
  const nameNodeRef    = useRef<SVGCircleElement>(null)
  const emailNodeRef   = useRef<SVGCircleElement>(null)
  const msgNodeRef     = useRef<SVGCircleElement>(null)
  const sendBtnRef     = useRef<HTMLButtonElement>(null)
  const arrowRef       = useRef<HTMLSpanElement>(null)
  const sendUnderlineRef = useRef<HTMLDivElement>(null)

  // ── Form state ──────────────────────────────────────────────────────────
  const [name,    setName]    = useState('')
  const [email,   setEmail]   = useState('')
  const [message, setMessage] = useState('')
  const [submitErr, setSubmitErr] = useState('')
  const [done,    setDone]    = useState(false)
  const [focusedField, setFocusedField] = useState<FieldName | null>(null)

  const reduced = useReducedMotion()

  // ── Horizon path lerp state ─────────────────────────────────────────────
  const widthRef    = useRef(0)
  const rectRef     = useRef<DOMRect | null>(null)
  const targetCxRef = useRef(0)
  const targetCyRef = useRef(CENTER_Y)
  const curCxRef    = useRef(0)
  const curCyRef    = useRef(CENTER_Y)
  const rafRef      = useRef(0)
  const hoverInSecRef = useRef(false)

  // ── Measure horizon + reposition focus nodes ───────────────────────────
  const updateNodeX = useCallback(() => {
    const hr = rectRef.current
    if (!hr) return
    const place = (circle: React.RefObject<SVGCircleElement | null>, wrap: React.RefObject<HTMLDivElement | null>) => {
      const f = wrap.current?.getBoundingClientRect()
      const c = circle.current
      if (!f || !c) return
      const x = f.left + f.width / 2 - hr.left
      c.setAttribute('cx', String(x))
    }
    place(nameNodeRef,  nameWrapRef)
    place(emailNodeRef, emailWrapRef)
    place(msgNodeRef,   msgWrapRef)
  }, [])

  const measure = useCallback(() => {
    const sv = horizonSvgRef.current
    if (!sv) return
    const r = sv.getBoundingClientRect()
    widthRef.current   = r.width
    rectRef.current    = r
    targetCxRef.current = r.width / 2
    curCxRef.current    = r.width / 2
    if (horizonPathRef.current) {
      horizonPathRef.current.setAttribute(
        'd', `M 0 ${CENTER_Y} Q ${r.width / 2} ${CENTER_Y} ${r.width} ${CENTER_Y}`,
      )
    }
    updateNodeX()
  }, [updateNodeX])

  // ── Resize listener ─────────────────────────────────────────────────────
  useEffect(() => {
    measure()
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [measure])

  // ── rAF loop: lerp control point toward target, update path ────────────
  useEffect(() => {
    if (reduced) return
    const tick = () => {
      curCxRef.current += (targetCxRef.current - curCxRef.current) * 0.12
      curCyRef.current += (targetCyRef.current - curCyRef.current) * 0.12
      const w = widthRef.current
      if (w > 0 && horizonPathRef.current) {
        horizonPathRef.current.setAttribute(
          'd', `M 0 ${CENTER_Y} Q ${curCxRef.current} ${curCyRef.current} ${w} ${CENTER_Y}`,
        )
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [reduced])

  // ── Mouse tracking — cursor proximity bowing ───────────────────────────
  useEffect(() => {
    if (reduced) return
    const sec = sectionRef.current
    if (!sec) return

    const onMove = (e: MouseEvent) => {
      const hr = rectRef.current
      if (!hr) return
      const relY = e.clientY - (hr.top + CENTER_Y)
      const dist = Math.abs(relY)
      const w = widthRef.current

      if (dist < 120 && hoverInSecRef.current) {
        const strength     = (120 - dist) / 120
        const displacement = Math.sign(relY) * strength * 12
        const ctrlY = CENTER_Y + 2 * displacement
        const ctrlX = Math.max(0, Math.min(w, e.clientX - hr.left))
        targetCxRef.current = ctrlX
        targetCyRef.current = ctrlY
      } else {
        targetCxRef.current = w / 2
        targetCyRef.current = CENTER_Y
      }
    }
    const onEnter = () => { hoverInSecRef.current = true }
    const onLeave = () => {
      hoverInSecRef.current = false
      targetCxRef.current = widthRef.current / 2
      targetCyRef.current = CENTER_Y
    }

    sec.addEventListener('mousemove', onMove)
    sec.addEventListener('mouseenter', onEnter)
    sec.addEventListener('mouseleave', onLeave)
    return () => {
      sec.removeEventListener('mousemove', onMove)
      sec.removeEventListener('mouseenter', onEnter)
      sec.removeEventListener('mouseleave', onLeave)
    }
  }, [reduced])

  // ── Focus node visibility driven by focus + content state ──────────────
  useEffect(() => {
    const sync = (
      ref: React.RefObject<SVGCircleElement | null>,
      isFocused: boolean,
      isFilled: boolean,
    ) => {
      const el = ref.current
      if (!el) return
      const op    = isFocused ? 1 : isFilled ? 0.5 : 0
      const scale = isFocused ? 1.25 : 1
      gsap.to(el, {
        autoAlpha: op, scale,
        duration: 0.35, ease: 'power2.out', transformOrigin: '50% 50%',
      })
    }
    sync(nameNodeRef,  focusedField === 'name',    name.trim().length > 0)
    sync(emailNodeRef, focusedField === 'email',   /\S+@\S+\.\S+/.test(email))
    sync(msgNodeRef,   focusedField === 'message', message.trim().length > 0)
  }, [focusedField, name, email, message])

  // ── Emit typing pulse from a field's horizontal position ───────────────
  const emitPulse = useCallback((wrapRef: React.RefObject<HTMLDivElement | null>) => {
    if (reduced) return
    const sec = sectionRef.current
    const w   = wrapRef.current
    const sv  = horizonSvgRef.current
    if (!sec || !w || !sv) return

    // Re-measure on every call — cheap, defensive against stale rects
    // (scroll, HMR, resize, Lenis smooth-scroll offsets).
    const secR = sec.getBoundingClientRect()
    const hr   = sv.getBoundingClientRect()
    const fr   = w.getBoundingClientRect()
    const originX = fr.left + fr.width / 2 - secR.left
    const y       = hr.top + CENTER_Y - secR.top

    ;[-1, 1].forEach(dir => {
      const dot = document.createElement('div')
      dot.style.cssText = `
        position:absolute;
        left:${originX}px; top:${y}px;
        width:5px; height:5px;
        border-radius:50%;
        background:${LIME};
        box-shadow:0 0 6px ${LIME}, 0 0 14px rgba(159,175,8,0.4);
        pointer-events:none;
        z-index:15;
      `
      sec.appendChild(dot)
      // xPercent/yPercent centers the dot — they survive GSAP's transform
      // rewrites when x/y are animated. Inline translate(-50%,-50%) would
      // be clobbered the moment GSAP's x tween fires.
      gsap.set(dot, { xPercent: -50, yPercent: -50 })
      gsap.to(dot, {
        x: dir * (180 + Math.random() * 80),
        opacity: 0,
        duration: 0.8,
        ease: 'power2.out',
        onComplete: () => dot.remove(),
      })
    })
  }, [reduced])

  // ── Spawn upward lime particles from random positions on the horizon ───
  const spawnParticles = useCallback((count: number) => {
    if (reduced) return
    const sec = sectionRef.current
    const sv  = horizonSvgRef.current
    if (!sec || !sv) return
    const secR = sec.getBoundingClientRect()
    const hr   = sv.getBoundingClientRect()
    const y    = hr.top + CENTER_Y - secR.top
    const ox   = hr.left - secR.left
    const w    = hr.width

    for (let i = 0; i < count; i++) {
      const dot = document.createElement('div')
      const size = 3 + Math.random() * 2
      dot.style.cssText = `
        position:absolute;
        left:${ox + Math.random() * w}px; top:${y}px;
        width:${size}px; height:${size}px;
        border-radius:50%;
        background:${LIME};
        box-shadow:0 0 6px ${LIME};
        pointer-events:none;
        z-index:15;
      `
      sec.appendChild(dot)
      gsap.set(dot, { xPercent: -50, yPercent: -50 })
      gsap.to(dot, {
        y: -(40 + Math.random() * 40),
        x: (Math.random() - 0.5) * 36,
        opacity: 0,
        duration: 1 + Math.random() * 0.6,
        ease: 'power2.out',
        onComplete: () => dot.remove(),
      })
    }
  }, [reduced])

  // Dedicated timeline refs so we can kill in-flight pulses cleanly
  // without the `onComplete: startBreath` callback firing on a killed TL.
  const horizonTlRef = useRef<gsap.core.Timeline | null>(null)
  const breathTweenRef = useRef<gsap.core.Tween | null>(null)

  // ── Ambient breath (opacity gently pulsing) ────────────────────────────
  const startBreath = useCallback(() => {
    const p = horizonPathRef.current
    if (!p || reduced) return
    breathTweenRef.current?.kill()
    breathTweenRef.current = gsap.to(p, {
      opacity: 0.6,
      duration: 2.0,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      overwrite: 'auto',
    })
  }, [reduced])

  // ── Horizon animations (hover / success / error) ───────────────────────
  const pulseHorizon = useCallback((strong = false) => {
    const p = horizonPathRef.current
    if (!p) return
    // Kill breath + any prior pulse TL so they can't fire onComplete later
    breathTweenRef.current?.kill()
    horizonTlRef.current?.kill()
    const tl = gsap.timeline({ onComplete: () => startBreath() })
    horizonTlRef.current = tl
    if (strong) {
      tl.to(p, { opacity: 0.95, filter: `drop-shadow(0 0 10px ${LIME})`, duration: 0.3, ease: 'power2.out' })
        .to(p, { opacity: 0.55, filter: `drop-shadow(0 0 4px rgba(159,175,8,0.35))`, duration: 0.9, ease: 'sine.out' })
    } else {
      tl.to(p, { opacity: 0.9, filter: `drop-shadow(0 0 6px ${LIME})`, duration: 0.3 })
        .to(p, { opacity: 0.5, filter: `drop-shadow(0 0 3px rgba(159,175,8,0.25))`, duration: 0.5 })
    }
  }, [startBreath])

  const errorFlash = useCallback(() => {
    if (reduced) return
    const p = horizonPathRef.current
    const b = horizonBoxRef.current
    if (!p || !b) return
    breathTweenRef.current?.kill()
    horizonTlRef.current?.kill()
    const tl = gsap.timeline({ onComplete: () => startBreath() })
    horizonTlRef.current = tl
    tl.to(p, { stroke: ERROR_C, opacity: 0.85, duration: 0.12 })
      .to(b, { x: -4, duration: 0.06 }, 0)
      .to(b, { x:  4, duration: 0.06 })
      .to(b, { x: -3, duration: 0.06 })
      .to(b, { x:  0, duration: 0.10, ease: 'power2.out' })
      .to(p, { stroke: OLIVE, opacity: 0.5, duration: 0.4 }, 0.35)
  }, [reduced, startBreath])

  // ── Form handlers ──────────────────────────────────────────────────────
  const handleFocus = (f: FieldName) => () => {
    setFocusedField(f)
    if (submitErr) setSubmitErr('')
  }
  const handleBlur = () => setFocusedField(null)

  const makeChange =
    <T extends HTMLInputElement | HTMLTextAreaElement>(setter: (v: string) => void, wrap: React.RefObject<HTMLDivElement | null>) =>
    (e: React.ChangeEvent<T>) => {
      setter(e.target.value)
      if (submitErr) setSubmitErr('')
      emitPulse(wrap)
    }

  const shakeField = useCallback((w: React.RefObject<HTMLDivElement | null>) => {
    const el = w.current
    if (!el || reduced) return
    gsap.killTweensOf(el, 'x')
    gsap.timeline()
      .to(el, { x: -4, duration: 0.05 })
      .to(el, { x:  4, duration: 0.05 })
      .to(el, { x: -3, duration: 0.05 })
      .to(el, { x:  0, duration: 0.10, ease: 'power2.out' })
  }, [reduced])

  const handleSendHover = useCallback(() => {
    if (reduced || done) return
    if (arrowRef.current)       gsap.to(arrowRef.current,       { x: 8, duration: 0.22, ease: 'power2.out' })
    if (sendUnderlineRef.current) gsap.to(sendUnderlineRef.current, { scaleX: 1, duration: 0.3, ease: 'power2.out' })
    pulseHorizon(false)
    spawnParticles(6)
  }, [reduced, done, pulseHorizon, spawnParticles])

  const handleSendLeave = useCallback(() => {
    if (arrowRef.current)       gsap.to(arrowRef.current,       { x: 0, duration: 0.22, ease: 'power2.out' })
    if (sendUnderlineRef.current && !done) gsap.to(sendUnderlineRef.current, { scaleX: 0, duration: 0.2 })
  }, [done])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    const nameOk  = !!name.trim()
    const emailOk = /\S+@\S+\.\S+/.test(email)

    if (!nameOk || !emailOk) {
      if      (!nameOk && !emailOk) setSubmitErr(t('errorGlobal'))
      else if (!nameOk)             setSubmitErr(t('errorName'))
      else                          setSubmitErr(t('errorEmail'))
      errorFlash()
      if (!nameOk)  shakeField(nameWrapRef)
      if (!emailOk) shakeField(emailWrapRef)
      return
    }

    setSubmitErr('')
    // Click feedback
    if (sendBtnRef.current) {
      gsap.to(sendBtnRef.current, { scale: 0.97, duration: 0.08, yoyo: true, repeat: 1 })
    }
    setDone(true)
    // Bloom + particle burst
    pulseHorizon(true)
    setTimeout(() => spawnParticles(18), 150)

    // TODO: replace with Formspree/Resend integration
    // Fallback: populate user's mail client
    const subject = t('mailSubject', { name })
    const body = `${message}\n\n— ${name}\n${email}`
    try {
      window.location.href =
        `mailto:biosoilterra@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    } catch { /* ignore — success state still shows */ }
  }, [name, email, message, errorFlash, pulseHorizon, spawnParticles, shakeField])

  // ── Entrance animation ─────────────────────────────────────────────────
  useGSAP(
    () => {
      gsapSetup()
      const sec = sectionRef.current
      if (!sec) return
      measure()

      if (reduced) {
        gsap.set('[data-ct-anim]', { autoAlpha: 1, y: 0 })
        gsap.set('[data-ct-crop]', { autoAlpha: 1 })
        if (horizonBoxRef.current)  gsap.set(horizonBoxRef.current,  { clipPath: 'inset(0 0% 0 0%)' })
        if (horizonPathRef.current) gsap.set(horizonPathRef.current, { opacity: 0.5 })
        return
      }

      gsap.set('[data-ct-tag]',      { autoAlpha: 0, y: 22 })
      gsap.set('[data-ct-eyebrow]',  { autoAlpha: 0, y: 10 })
      gsap.set('[data-ct-field]',    { autoAlpha: 0, y: 14 })
      gsap.set('[data-ct-underline]', { scaleX: 0, transformOrigin: 'left center' })
      gsap.set('[data-ct-lower]',    { autoAlpha: 0, y: 10 })
      gsap.set('[data-ct-crop]',     { autoAlpha: 0 })

      if (horizonBoxRef.current)  gsap.set(horizonBoxRef.current,  { clipPath: 'inset(0 50% 0 50%)' })
      if (horizonPathRef.current) gsap.set(horizonPathRef.current, { opacity: 0 })

      const tl = gsap.timeline({
        paused: true,
        defaults: { ease: 'power2.out' },
        onComplete: () => startBreath(),
      })

      // Horizon draws from center outward
      tl.to(horizonPathRef.current, { opacity: 0.5, duration: 0.3 }, 0.2)
      tl.to(horizonBoxRef.current, {
        clipPath: 'inset(0 0% 0 0%)',
        duration: 1.2,
        ease: 'power3.inOut',
      }, 0.2)

      // Tagline word-by-line stagger
      tl.to('[data-ct-tag]', { autoAlpha: 1, y: 0, duration: 0.55, stagger: 0.14 }, 0.6)

      // Eyebrow
      tl.to('[data-ct-eyebrow]', { autoAlpha: 1, y: 0, duration: 0.4 }, 1.2)

      // Form underlines draw left-to-right, then fields fade in
      tl.to('[data-ct-underline]', { scaleX: 1, duration: 0.5, stagger: 0.1, ease: 'power2.out' }, 1.4)
      tl.to('[data-ct-field]',     { autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.1 }, 1.5)

      // Lower half
      tl.to('[data-ct-lower]', { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.08 }, 1.8)

      // Crop marks last
      tl.to('[data-ct-crop]', { autoAlpha: 1, duration: 0.5 }, 2.2)

      ScrollTrigger.create({
        trigger: sec,
        start: 'top 70%',
        once: true,
        onEnter: () => tl.play(),
      })

      ScrollTrigger.refresh()
    },
    { scope: sectionRef, dependencies: [reduced, measure, startBreath] },
  )

  // ── Shared input styles ─────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: CREAM,
    fontFamily: FONT_BODY,
    fontSize: '1rem',
    outline: 'none',
    padding: 0,
  }

  const linkStyle: React.CSSProperties = {
    fontFamily: FONT_BODY,
    fontSize: '0.88rem',
    color: CREAM_75,
    textDecoration: 'none',
    display: 'inline-block',
    position: 'relative',
    paddingBottom: 2,
    transition: 'color 0.25s',
  }

  const fieldBorderColor = (isErr: boolean) => isErr ? 'rgba(178,68,40,0.4)' : CREAM_30

  return (
    <section
      ref={sectionRef}
      id="contact"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: 640,
        maxHeight: '80vh',
        display: 'flex',
        background: `linear-gradient(180deg, ${EARTH_TOP} 0%, ${EARTH_BOT} 100%)`,
        overflow: 'hidden',
      }}
    >
      <style>{`
        #contact input::placeholder,
        #contact textarea::placeholder {
          color: ${CREAM_40};
          transition: opacity 0.2s;
        }
        #contact input:focus::placeholder,
        #contact textarea:focus::placeholder { opacity: 0; }

        #contact .ct-link::after {
          content: '';
          position: absolute;
          left: 0; bottom: 0;
          width: 100%; height: 1px;
          background: ${LIME};
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.3s ease;
        }
        #contact .ct-link:hover::after { transform: scaleX(1); }

        @media (hover: none) and (pointer: coarse) {
          #contact .ct-send-arrow { transition: none; }
        }

        @media (max-width: 1023px) {
          #contact .ct-grid, #contact .ct-lower {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          #contact .ct-content {
            padding-top: 48px !important;
            padding-bottom: 48px !important;
          }
        }

        @media (max-width: 768px) {
          #contact [data-ct-tag],
          #contact .ct-grid p[style*="font-display"] {
            font-size: clamp(36px, 9.4vw, 56px) !important;
            line-height: 1.04 !important;
          }
          #contact [data-ct-eyebrow] {
            font-size: 10px !important;
            letter-spacing: 0.32em !important;
            margin-top: 18px !important;
          }
          #contact input,
          #contact textarea {
            font-size: 16px !important; /* Prevents iOS zoom on focus */
            min-height: 44px !important;
          }
          #contact textarea {
            min-height: 96px !important;
          }
          #contact .ct-link {
            font-size: 15px !important;
            min-height: 24px;
          }
          /* Horizon SVG line + nodes are decorative — keep but compact */
          #contact .ct-grid {
            gap: 24px !important;
          }
          #contact .ct-lower {
            gap: 24px !important;
            padding-top: 24px !important;
          }
        }
      `}</style>

      {/* Grain */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/grain.svg)',
          backgroundSize: '200px 200px',
          opacity: 0.07,
          mixBlendMode: 'screen',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <form
        ref={contentRef}
        onSubmit={handleSubmit}
        noValidate
        className="ct-content"
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 1280,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          padding: 'clamp(40px, 5vw, 72px) clamp(28px, 4vw, 56px)',
        }}
      >
        <CropBracket corner="tl" />
        <CropBracket corner="tr" />
        <CropBracket corner="bl" />
        <CropBracket corner="br" />

        {/* ── TOP HALF (above horizon) ─────────────────────────────── */}
        <div
          className="ct-grid"
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'clamp(32px, 6vw, 96px)',
            alignItems: 'start',
            paddingBottom: 'clamp(24px, 3vw, 40px)',
          }}
        >
          {/* Tagline + eyebrow */}
          <div>
            <p style={{
              fontFamily: FONT_DISP,
              fontWeight: 500,
              fontSize: 'clamp(2.4rem, 5vw, 4.5rem)',
              lineHeight: 1.0,
              letterSpacing: '-0.02em',
              color: CREAM,
              margin: 0,
            }}>
              <span data-ct-tag style={{ display: 'block' }}>{t('taglineLine1')}</span>
              <span data-ct-tag style={{ display: 'block' }}>{t('taglineLine2')}</span>
              <span data-ct-tag style={{ display: 'block' }}>
                {t('taglineLine3Pre')}{' '}
                <span style={{
                  fontFamily: FONT_SERIF,
                  fontStyle: 'italic',
                  fontWeight: 400,
                  color: LIME,
                }}>{t('taglineLine3Italic').replace(/\.$/, '')}</span>
                <span style={{ color: LIME, fontSize: '1.1em' }}>.</span>
              </span>
            </p>

            <p data-ct-eyebrow style={{
              fontFamily: FONT_BODY,
              fontSize: '0.66rem',
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: OLIVE,
              margin: 'clamp(24px, 3vw, 32px) 0 0',
            }}>
              {t('eyebrow')}
            </p>
          </div>

          {/* Form fields (or success message) */}
          <div>
            {done ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{
                  fontFamily: FONT_DISP,
                  fontSize: 'clamp(1.5rem, 2.6vw, 2.2rem)',
                  fontWeight: 500,
                  lineHeight: 1.1,
                  color: CREAM,
                  margin: 0,
                }}>
                  {t('successLine1')}
                </p>
                <p style={{
                  fontFamily: FONT_SERIF,
                  fontStyle: 'italic',
                  fontSize: 'clamp(1.15rem, 1.9vw, 1.65rem)',
                  lineHeight: 1.2,
                  color: LIME,
                  margin: 0,
                }}>
                  {t('successLine2')}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                {/* Name */}
                <div
                  ref={nameWrapRef}
                  data-ct-field
                  style={{ position: 'relative', paddingBottom: 8 }}
                >
                  <input
                    ref={nameInputRef}
                    type="text"
                    placeholder={t('placeholderName')}
                    aria-label={t('ariaName')}
                    value={name}
                    onChange={makeChange(setName, nameWrapRef)}
                    onFocus={handleFocus('name')}
                    onBlur={handleBlur}
                    style={{ ...inputStyle, height: 40 }}
                  />
                  <div
                    data-ct-underline
                    style={{
                      position: 'absolute', left: 0, bottom: 0,
                      height: 1, width: '100%',
                      background: fieldBorderColor(false),
                      transformOrigin: 'left center',
                    }}
                  />
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute', left: 0, bottom: 0,
                      height: 1.5,
                      width: focusedField === 'name' ? '100%' : '0%',
                      background: LIME,
                      transition: 'width 0.3s ease',
                      pointerEvents: 'none',
                    }}
                  />
                </div>

                {/* Email */}
                <div
                  ref={emailWrapRef}
                  data-ct-field
                  style={{ position: 'relative', paddingBottom: 8 }}
                >
                  <input
                    ref={emailInputRef}
                    type="email"
                    placeholder={t('placeholderEmail')}
                    aria-label={t('ariaEmail')}
                    value={email}
                    onChange={makeChange(setEmail, emailWrapRef)}
                    onFocus={handleFocus('email')}
                    onBlur={handleBlur}
                    style={{ ...inputStyle, height: 40 }}
                  />
                  <div
                    data-ct-underline
                    style={{
                      position: 'absolute', left: 0, bottom: 0,
                      height: 1, width: '100%',
                      background: fieldBorderColor(false),
                      transformOrigin: 'left center',
                    }}
                  />
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute', left: 0, bottom: 0,
                      height: 1.5,
                      width: focusedField === 'email' ? '100%' : '0%',
                      background: LIME,
                      transition: 'width 0.3s ease',
                      pointerEvents: 'none',
                    }}
                  />
                </div>

                {/* Message */}
                <div
                  ref={msgWrapRef}
                  data-ct-field
                  style={{ position: 'relative', paddingBottom: 8 }}
                >
                  <textarea
                    ref={msgInputRef}
                    placeholder={t('placeholderMessage')}
                    aria-label={t('ariaMessage')}
                    value={message}
                    onChange={makeChange(setMessage, msgWrapRef)}
                    onFocus={handleFocus('message')}
                    onBlur={handleBlur}
                    rows={3}
                    style={{
                      ...inputStyle,
                      height: 96,
                      resize: 'none',
                      lineHeight: 1.55,
                    }}
                  />
                  <div
                    data-ct-underline
                    style={{
                      position: 'absolute', left: 0, bottom: 0,
                      height: 1, width: '100%',
                      background: fieldBorderColor(false),
                      transformOrigin: 'left center',
                    }}
                  />
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute', left: 0, bottom: 0,
                      height: 1.5,
                      width: focusedField === 'message' ? '100%' : '0%',
                      background: LIME,
                      transition: 'width 0.3s ease',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── HORIZON LINE ─────────────────────────────────────────── */}
        <div
          ref={horizonBoxRef}
          aria-hidden="true"
          style={{
            position: 'relative',
            width: '100%',
            height: HORIZON_H,
            pointerEvents: 'none',
            clipPath: 'inset(0 50% 0 50%)',
          }}
        >
          <svg
            ref={horizonSvgRef}
            style={{
              position: 'absolute',
              left: 0, top: 0,
              width: '100%', height: '100%',
              overflow: 'visible',
            }}
          >
            <path
              ref={horizonPathRef}
              d={`M 0 ${CENTER_Y} Q 0 ${CENTER_Y} 0 ${CENTER_Y}`}
              stroke={OLIVE}
              strokeWidth={1.5}
              fill="none"
              strokeLinecap="round"
              style={{
                filter: `drop-shadow(0 0 3px rgba(159,175,8,0.25))`,
                opacity: 0,
              }}
            />
            <circle ref={nameNodeRef}  cx={0} cy={CENTER_Y} r={3.5} fill={LIME}
              style={{ filter: `drop-shadow(0 0 5px ${LIME})`, opacity: 0 }} />
            <circle ref={emailNodeRef} cx={0} cy={CENTER_Y} r={3.5} fill={LIME}
              style={{ filter: `drop-shadow(0 0 5px ${LIME})`, opacity: 0 }} />
            <circle ref={msgNodeRef}   cx={0} cy={CENTER_Y} r={3.5} fill={LIME}
              style={{ filter: `drop-shadow(0 0 5px ${LIME})`, opacity: 0 }} />
          </svg>
        </div>

        {/* ── BOTTOM HALF (below horizon) ─────────────────────────── */}
        <div
          className="ct-lower"
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'clamp(32px, 6vw, 96px)',
            alignItems: 'start',
            paddingTop: 'clamp(24px, 3vw, 40px)',
          }}
        >
          {/* Contact lines */}
          <div data-ct-lower style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <a href="mailto:biosoilterra@gmail.com" className="ct-link" style={linkStyle}>
              biosoilterra@gmail.com
            </a>
            <a href="tel:+34618124042" className="ct-link" style={linkStyle}>
              +34 618 124 042
            </a>
            <a
              href="https://www.google.com/maps/search/UPV+Valencia"
              target="_blank" rel="noopener noreferrer"
              className="ct-link" style={linkStyle}
            >
              {t('mapLocation')}
            </a>
            <a
              href="https://www.linkedin.com/company/biosoil"
              target="_blank" rel="noopener noreferrer"
              className="ct-link"
              style={{ ...linkStyle, marginTop: 6 }}
            >
              {t('linkedIn')}
            </a>
          </div>

          {/* Send + error */}
          <div data-ct-lower style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
            {!done && (
              <>
                <button
                  ref={sendBtnRef}
                  type="submit"
                  onMouseEnter={handleSendHover}
                  onMouseLeave={handleSendLeave}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: 12,
                    position: 'relative',
                    fontFamily: FONT_DISP,
                    fontSize: 'clamp(1.2rem, 2vw, 1.7rem)',
                    fontWeight: 500,
                    color: CREAM,
                    letterSpacing: '-0.01em',
                  }}
                >
                  <span>{t('sendLabel')}</span>
                  <span
                    ref={arrowRef}
                    className="ct-send-arrow"
                    style={{ display: 'inline-block', fontFamily: FONT_DISP }}
                  >
                    →
                  </span>
                  <div
                    ref={sendUnderlineRef}
                    style={{
                      position: 'absolute',
                      left: 0, bottom: -5,
                      width: '100%', height: 1,
                      background: LIME,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left center',
                      pointerEvents: 'none',
                    }}
                  />
                </button>
                {submitErr && (
                  <p style={{
                    fontFamily: FONT_BODY,
                    fontSize: '0.68rem',
                    letterSpacing: '0.24em',
                    textTransform: 'uppercase',
                    color: ERROR_C,
                    margin: '4px 0 0',
                  }}>
                    {submitErr}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </form>
    </section>
  )
}
